import React, { useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  Grid,
  Typography,
  FormControlLabel,
  Box,
  Button,
  IconButton,
  Divider,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  TextField,
  MenuItem,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import StorefrontIcon from '@material-ui/icons/Storefront';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import PaymentIcon from '@material-ui/icons/Payment';
import LabelIcon from '@material-ui/icons/Label';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { SnackbarProvider } from 'notistack';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { CustomInput, CustomSwitch, Toaster, NumberFormatCustom } from '@/components';
import { ActionPopUp } from '@/Layouts';
import {
  createOrUpdateProductServiceConfigService,
} from '@/services/productServiceConfig';
import type { PaymentMethod } from '../PaymentMethodsScreen';
import { useStyles } from './PaymentMethodForm.styles';

const PRODUCT_TYPE_OPTIONS = [
  { value: 'PAYMENT_LINK', label: 'PAYMENT_LINK', disabled: false },
  { value: 'BILLET_TAP', label: 'BILLET_TAP', disabled: false },
  { value: 'MPOS', label: 'MPOS', disabled: true },
  { value: 'SMARTPOS', label: 'SMARTPOS', disabled: true },
];

interface PaymentMethodFormProps {
  open: boolean;
  title: string;
  data: PaymentMethod | null;
  defaultValues: Partial<PaymentMethod>;
  onClose: () => void;
  refreshData: () => void;
  readOnly?: boolean;
}

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  open,
  title,
  data,
  defaultValues,
  onClose,
  refreshData,
  readOnly = false,
}) => {
  const classes = useStyles(); 
  const providerRef = React.useRef<any>(null);
  const isEditing = Boolean(data?.sk);
  const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', text: '' });
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showFreeModal, setShowFreeModal] = useState(false);
  const [draggedMethod, setDraggedMethod] = useState<string | null>(null);
  const [dragOverMethod, setDragOverMethod] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const PAYMENT_METHODS_CONFIG: { key: string; label: string }[] = [
    { key: 'card', label: 'Tarjeta' },
    { key: 'paymentButton', label: 'Botón de pago' },
    { key: 'transfer', label: 'Transferencia' },
  ];

  const {
    handleSubmit,
    control,
    errors,
    reset,
    watch,
    setValue,
    trigger,
  } = useForm<any>({ 
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'features',
  });

  const terminalType = watch('terminalType');
  const pricingModel = watch('pricingModel');
  const formValues = watch(); // Observar todos los valores del formulario para debug

  useEffect(() => {
    if (open) {
      const initialData = data || defaultValues;
      // Agrear al menos una característica vacía si no existen para evitar problemas con useFieldArray
      if (!initialData.features || initialData.features.length === 0) {
        initialData.features = [{ value: '' }];
      }

      reset(initialData);
      // Forzar el valor de product_type después del reset ya que CustomSelect tiene defaultValue="" hardcodeado
      const productTypeValue = initialData.product_type || 'BILLET_TAP';
      setValue('product_type', productTypeValue);
    }
  }, [open, data, defaultValues, reset, setValue]);

  useEffect(() => {
    if (pricingModel === 'FREE') {
      setValue('price', 0);
    }

    if (pricingModel !== 'ONE_PAY') {
      setValue('paymentMethods.card', false);
      setValue('paymentMethods.paymentButton', false);
      setValue('paymentMethods.transfer', false);
    }
  }, [pricingModel, setValue]);

  const validatePaymentMethods = () => {
    if (pricingModel !== 'ONE_PAY') return true;
    const methods = watch('paymentMethods') || {};
    const activeCount = Object.values(methods).filter(Boolean).length;
    return activeCount > 0 || 'Debe habilitar al menos un método de pago';
  };

  const handlePaymentMethodToggle = (methodKey: string, currentValue: boolean) => {
    if (pricingModel !== 'ONE_PAY') return;

    const currentMethods = watch('paymentMethods') || {};
    const activeMethodsCount = Object.values(currentMethods).filter(Boolean).length;

    if (currentValue && activeMethodsCount === 1) {
      setShowWarningModal(true);
      return;
    }

    setValue(`paymentMethods.${methodKey}`, !currentValue, { shouldValidate: true });
  };

  const handleWarningClose = () => setShowWarningModal(false);
  const handleFreeModalClose = () => setShowFreeModal(false);

  const handlePricingModelChange = (newValue: string, onChange: (val: string) => void) => {
    onChange(newValue);
    if (newValue === 'FREE') {
      setShowFreeModal(true);
    }
  };

  // ── Drag & Drop handlers ──
  const handleDragStart = (key: string) => (e: React.DragEvent) => {
    setDraggedMethod(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  };

  const handleDragOver = (key: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedMethod && key !== draggedMethod) {
      setDragOverMethod(key);
    }
  };

  const handleDragLeave = () => {
    setDragOverMethod(null);
  };

  const handleDrop = (targetKey: string) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverMethod(null);
    if (!draggedMethod || draggedMethod === targetKey) return;

    const currentOrder: string[] = watch('paymentMethodsOrder') || PAYMENT_METHODS_CONFIG.map(m => m.key);
    const fromIndex = currentOrder.indexOf(draggedMethod);
    const toIndex = currentOrder.indexOf(targetKey);
    if (fromIndex === -1 || toIndex === -1) return;

    const newOrder = [...currentOrder];
    newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, draggedMethod);
    setValue('paymentMethodsOrder', newOrder);
    setDraggedMethod(null);
  };

  const handleDragEnd = () => {
    setDraggedMethod(null);
    setDragOverMethod(null);
  };

  const onSubmit = async (formData: any) => {
    setLoading(true);
    try {
      const productName = formData.product_name?.trim();
      const productType = formData.product_type?.trim();

      if (!productName) {
        setAlert({ show: true, type: 'error', text: 'El nombre del medio es requerido.' });
        setLoading(false);
        return;
      }

      if (!productType) {
        setAlert({ show: true, type: 'error', text: 'El tipo de producto es requerido.' });
        setLoading(false);
        return;
      }

      const methodKeyMap: Record<string, string> = {
        card: 'CREDIT_CARD',
        paymentButton: 'BHD_BUTTON',
        transfer: 'BANK_TRANSFER',
      };
      const orderedKeys: string[] = formData.paymentMethodsOrder || ['card', 'paymentButton', 'transfer'];
      const isOnePay = formData.pricingModel === 'ONE_PAY';
      const paymentMethodsSupported = orderedKeys
        .map((key: string, index: number) => ({
          method: methodKeyMap[key],
          order: index + 1,
          status: isOnePay && formData.paymentMethods[key] ? 'ACTIVE' : 'INACTIVE',
        }));

      const features = (formData.features || [])
        .map((f: any) => typeof f === 'string' ? f : f.value)
        .filter((f: string) => f.trim() !== '');

      const payload = {
        ...(formData.sk ? { sk: formData.sk } : {}),
        product_name: productName,
        product_type: productType,
        config: {
          base: {
            type: [formData.terminalType],
            acquisition: {
              payment_methods_supported: paymentMethodsSupported,
            },
            active: formData.active,
            currency: 'DOP',
            description: formData.description,
            fulfillment_type: formData.terminalType === 'DISPOSITIVO' ? 'DELIVERY' : 'DIGITAL',
            labels: {
              not_requested: formData.labels?.notRequested || '',
              features: features,
              requested: formData.labels?.requested || '',
            },
            product_type: productType,
            requires_fulfillment: formData.terminalType === 'DISPOSITIVO',
            status: formData.active ? 'ACTIVE' : 'INACTIVE',
          },
          pricing: {
            amount: parseFloat(formData.price),
            currency: 'DOP',
            model: [formData.pricingModel],
            product_type: productType,
            status: 'ACTIVE',
          },
        },
      };

      const res = await createOrUpdateProductServiceConfigService(payload);

      if (res?.status === 200 || res?.status === 201) {
        const successMsg = isEditing
          ? 'Configuración actualizada exitosamente'
          : 'Configuración creada exitosamente';
        setAlert({ show: true, type: 'success', text: successMsg });
        refreshData();
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setAlert({ show: true, type: 'error', text: 'Ha ocurrido un error, intente nuevamente.' });
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setAlert({ show: true, type: 'error', text: 'Ha ocurrido un error, intente nuevamente.' });
    } finally {
      setLoading(false);
    }
  };

  const validatePrice = (value: number) => {
    if (pricingModel === 'FREE') return true;
    if (value < 0) return 'El precio no puede ser negativo';
    if (pricingModel === 'ONE_PAY' || pricingModel === 'RENTAL') {
      if (!value || value <= 0) return 'El precio debe ser mayor a 0';
    }
    if (value.toString().split('.')[0]?.length > 5) return 'El precio máximo es 99,999.99';
    if (value.toString().split('.')[1]?.length > 2) return 'Solo se aceptan 2 decimales';
    return true;
  };

  const validateQuantity = (value: number) => {
    if (terminalType === 'DISPOSITIVO') {
      if (!value || value <= 0) return 'La cantidad debe ser mayor a 0';
      if (value % 1 !== 0) return 'La cantidad debe ser un número entero';
    }
    return true;
  };

  const validateProductName = (value: string) => {
    if (!value) return 'El nombre es requerido';
    if (value.length > 60) return 'El nombre no puede exceder 60 caracteres';
    if (!/^[a-zA-Z0-9\u00C0-\u00FF ]+$/.test(value)) {
      return 'Solo se permiten letras, números, tildes y espacios';
    }
    if (/\s\s/.test(value)) return 'No puede contener más de 2 espacios consecutivos';
    return true;
  };

  if (!control) return null;

  const handleFormSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const isValid = await trigger();
    if (!isValid) {
      setAlert({ 
        show: true, 
        type: 'error', 
        text: 'Por favor, complete todos los campos obligatorios correctamente.' 
      });
      return;
    }
    handleSubmit(onSubmit)(e);
  };

  return (
    <>
      <SnackbarProvider ref={providerRef} maxSnack={3}>
        <ActionPopUp
          title={title}
          open={open}
          onClose={onClose}
          onSave={handleFormSubmit}
          enabledControls={!loading}
          // loading={loading}
          readOnly={readOnly}
          withoutSaveButton={readOnly}
        >
          <Box py={1}>
            {/* Campo oculto para preservar el sk en edición */}
            <Controller
              name="sk"
              control={control}
              defaultValue=""
              render={() => <input type="hidden" />}
            />
            <Grid container spacing={3}>

              {/* ═══════════════ SECCIÓN: IDENTIFICACIÓN ═══════════════ */}
              <Grid item xs={12}>
                <Box className={classes.sectionHeader}>
                  <StorefrontIcon className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Identificación del Producto
                  </Typography>
                </Box>
                <Box className={classes.sectionCard}>
                  <Grid container spacing={2}>
                    {/* Product Type */}
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.fieldLabel}>
                        Producto ID<span style={{ color: '#d61103' }}>*</span>
                      </Typography>
                      <Controller
                        name="product_type"
                        control={control}
                        defaultValue={defaultValues.product_type || ''}
                        rules={{ required: 'Seleccione un id de producto' }}
                        render={({ value, onChange }) => (
                          <TextField
                            select
                            size="small"
                            variant="outlined"
                            fullWidth
                            placeholder="Seleccione un producto"
                            value={value || ''}
                            onChange={onChange}
                            disabled={isEditing}
                            error={Boolean(errors.product_type)}
                            helperText={
                              isEditing
                                ? 'El producto no se puede cambiar en modo edición'
                                : errors.product_type?.message
                            }
                            FormHelperTextProps={{
                              style: {
                                margin: '0 0 0 5px',
                                color: isEditing ? '#666' : '#d61103',
                              },
                            }}
                          >
                            {PRODUCT_TYPE_OPTIONS.map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled || false}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>

                    {/* Product Name */}
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.fieldLabel}>
                        Nombre del medio de pago
                        {' '}<span style={{ color: '#d61103' }}>*</span>
                      </Typography>
                      <CustomInput
                        name="product_name"
                        control={control}
                        label=""
                        error={errors}
                        rule={{
                          required: 'El nombre es requerido',
                          validate: validateProductName,
                        }}
                        placeholder="Ej: Link de pagos, Terminal Billet..."
                        inputProps={{ maxLength: 60 }}
                      />
                    </Grid>

                    {/* Descripción */}
                    <Grid item xs={12}>
                      <Typography className={classes.fieldLabel}>
                        Descripción <span style={{ color: '#d61103' }}>*</span>
                      </Typography>
                      <CustomInput
                        name="description"
                        control={control}
                        label=""
                        error={errors}
                        rule={{ 
                          required: 'La descripción es requerida',
                          maxLength: { value: 500, message: 'La descripción no puede exceder 500 caracteres' }
                        }}
                        multiline
                        rows={3}
                        placeholder="Describe brevemente este medio de pago..."
                        inputProps={{ maxLength: 500 }}
                      />
                    </Grid>

                    {/* Estado */}
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gridGap={12}>
                        <Typography className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                          Estado
                        </Typography>
                        <Controller
                          name="active"
                          control={control}
                          render={(props) => (
                            <Box display="flex" alignItems="center" gridGap={8}>
                              <CustomSwitch
                                check={props.value}
                                handleChange={(e: any) => props.onChange(e.target.checked)}
                                checked={props.value}
                                lblOff=""
                                lblOn=""
                              />
                              <Chip
                                label={props.value ? 'Activo' : 'Inactivo'}
                                size="small"
                                className={classes.statusChip}
                                style={{
                                  backgroundColor: props.value ? '#E8F5E9' : '#FFEBEE',
                                  color: props.value ? '#2E7D32' : '#C62828',
                                }}
                              />
                            </Box>
                          )}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}><Divider className={classes.divider} /></Grid>

              {/* ═══════════════ SECCIÓN: TIPO DE TERMINAL ═══════════════ */}
              <Grid item xs={12}>
                <Box className={classes.sectionHeader}>
                  <StorefrontIcon className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Tipo de Terminal
                  </Typography>
                </Box>
                <Box className={classes.sectionCard}>
                  <Typography className={classes.fieldLabel}>
                    Categorización del producto <span style={{ color: '#d61103' }}>*</span>
                  </Typography>
                  <Controller
                    name="terminalType"
                    control={control}
                    rules={{ required: 'Seleccione un tipo de terminal' }}
                    render={({ value, onChange }) => (
                      <Box>
                        <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
                          <Box className={classes.checkboxRow}>
                            <FormControlLabel
                              value="DISPOSITIVO"
                              control={<Radio className={classes.greenRadio} />}
                              label="Dispositivo Físico"
                            />
                            <FormControlLabel
                              value="ECOMMERCE"
                              control={<Radio className={classes.greenRadio} />}
                              label="eCommerce"
                            />
                          </Box>
                        </RadioGroup>
                        {errors.terminalType && (
                          <Typography color="error" variant="caption">
                            {errors.terminalType.message}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}><Divider className={classes.divider} /></Grid>

              {/* ═══════════════ SECCIÓN: PRECIO Y MODELO ═══════════════ */}
              <Grid item xs={12}>
                <Box className={classes.sectionHeader}>
                  <AttachMoneyIcon className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Precio y Modelo de Pago
                  </Typography>
                </Box>
                <Box className={classes.sectionCard}>
                  <Grid container spacing={2}>
                    {/* Precio */}
                    <Grid item xs={12} md={terminalType === 'DISPOSITIVO' ? 4 : 6}>
                      <Typography className={classes.fieldLabel}>
                        Precio (DOP) <span style={{ color: '#d61103' }}>*</span>
                      </Typography>
                      <CustomInput
                        name="price"
                        control={control}
                        label=""
                        error={errors}
                        rule={{
                          required: pricingModel !== 'FREE' ? 'El precio es requerido' : false,
                          validate: validatePrice,
                        }}
                        type="text"
                        disabled={pricingModel === 'FREE'}
                        placeholder="0.00"
                        InputPropsParam={{
                          inputComponent: NumberFormatCustom as any,
                          inputProps: {
                            isAllowed: (values: any) => {
                              const { floatValue } = values;
                              if (floatValue === undefined || floatValue === null) return true;
                              return floatValue >= 0 && floatValue <= 99999.99;
                            },
                            decimalScale: 2,
                            allowNegative: false,
                          },
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="body2" color="textSecondary">DOP</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {pricingModel === 'FREE' && (
                        <Typography className={classes.fieldHint}>
                          El precio es $0.00 porque el modelo es &quot;Gratis&quot;.
                        </Typography>
                      )}
                      {(pricingModel === 'ONE_PAY' || pricingModel === 'RENTAL') && (
                        <Typography className={classes.fieldHint}>
                          El precio debe ser mayor a 0 para este modelo de pago. Máximo: 99,999.99
                        </Typography>
                      )}
                    </Grid>

                    {/* Cantidad — Solo para dispositivo físico */}
                    {terminalType === 'DISPOSITIVO' && (
                      <Grid item xs={12} md={4}>
                        <Typography className={classes.fieldLabel}>
                          Cantidad <span style={{ color: '#d61103' }}>*</span>
                        </Typography>
                        <CustomInput
                          name="quantity"
                          control={control}
                          label=""
                          error={errors}
                          rule={{
                            required: 'La cantidad es requerida',
                            validate: validateQuantity,
                          }}
                          type="number"
                          inputProps={{ step: '1', min: '1' }}
                          placeholder="1"
                        />
                      </Grid>
                    )}

                    {/* Modelo de pago */}
                    <Grid item xs={12} md={terminalType === 'DISPOSITIVO' ? 4 : 6}>
                      <Typography className={classes.fieldLabel}>
                        Modelo de pago <span style={{ color: '#d61103' }}>*</span>
                      </Typography>
                      <Controller
                        name="pricingModel"
                        control={control}
                        rules={{ required: 'Seleccione un modelo de pago' }}
                        render={({ value, onChange }) => (
                          <Box>
                            <RadioGroup
                              value={value}
                              onChange={(e) => handlePricingModelChange(e.target.value, onChange)}
                            >
                              <Box className={classes.checkboxRow}>
                                <FormControlLabel
                                  value="ONE_PAY"
                                  control={<Radio className={classes.greenRadio} size="small" />}
                                  label={<Typography variant="body2">Pago Único</Typography>}
                                />
                                <FormControlLabel
                                  value="RENTAL"
                                  control={<Radio className={classes.greenRadio} size="small" />}
                                  label={<Typography variant="body2">Alquiler</Typography>}
                                />
                                <FormControlLabel
                                  value="FREE"
                                  control={<Radio className={classes.greenRadio} size="small" />}
                                  label={<Typography variant="body2">Gratis</Typography>}
                                />
                              </Box>
                            </RadioGroup>
                            {errors.pricingModel && (
                              <Typography color="error" variant="caption">
                                {errors.pricingModel.message}
                              </Typography>
                            )}
                          </Box>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12}><Divider className={classes.divider} /></Grid>

              {/* ═══════════════ SECCIÓN: MÉTODOS DE PAGO ═══════════════ */}
              <Grid item xs={12}>
                <Box className={classes.sectionHeader}>
                  <PaymentIcon className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Métodos de Pagos
                  </Typography>
                </Box>
                <Box className={classes.sectionCard}>
                  <Typography className={classes.fieldHint} style={{ marginBottom: 12 }}>
                    {pricingModel === 'ONE_PAY'
                      ? 'Seleccione los métodos de pago y arrastre para definir el orden (1 a 3 de izquierda a derecha). Debe haber al menos uno activo.'
                      : 'Los métodos de pago solo están disponibles para el modelo "Pago Único". Se enviarán como inactivos.'}
                  </Typography>
                  <Controller
                    name="paymentMethodsOrder"
                    control={control}
                    defaultValue={PAYMENT_METHODS_CONFIG.map(m => m.key)}
                    render={() => <input type="hidden" />}
                  />
                  <Grid container spacing={2}>
                    {(watch('paymentMethodsOrder') || PAYMENT_METHODS_CONFIG.map(m => m.key)).map((methodKey: string, index: number) => {
                      const config = PAYMENT_METHODS_CONFIG.find(m => m.key === methodKey);
                      if (!config) return null;
                      const isDragging = draggedMethod === methodKey;
                      const isDragOver = dragOverMethod === methodKey;
                      return (
                        <Grid item xs={12} sm={4} key={methodKey}>
                          <Box
                            className={`${classes.paymentMethodCard} ${isDragging ? classes.paymentMethodCardDragging : ''} ${isDragOver ? classes.paymentMethodCardDragOver : ''}`}
                            draggable={pricingModel === 'ONE_PAY'}
                            onDragStart={pricingModel === 'ONE_PAY' ? handleDragStart(methodKey) : undefined}
                            onDragOver={pricingModel === 'ONE_PAY' ? handleDragOver(methodKey) : undefined}
                            onDragLeave={pricingModel === 'ONE_PAY' ? handleDragLeave : undefined}
                            onDrop={pricingModel === 'ONE_PAY' ? handleDrop(methodKey) : undefined}
                            onDragEnd={pricingModel === 'ONE_PAY' ? handleDragEnd : undefined}
                            style={pricingModel !== 'ONE_PAY' ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                          >
                            <Box className={classes.orderBadge} style={pricingModel !== 'ONE_PAY' ? { backgroundColor: '#9E9E9E' } : undefined}>
                              {index + 1}
                            </Box>
                            <Box display="flex" alignItems="center">
                              <DragIndicatorIcon className={classes.dragHandle} style={pricingModel !== 'ONE_PAY' ? { opacity: 0.3 } : undefined} />
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                {config.label}
                              </Typography>
                            </Box>
                            <Controller
                              name={`paymentMethods.${methodKey}`}
                              control={control}
                              rules={methodKey === 'card' ? { validate: validatePaymentMethods } : undefined}
                              render={(props) => (
                                <CustomSwitch
                                  check={props.value}
                                  handleChange={() => handlePaymentMethodToggle(methodKey, props.value)}
                                  checked={props.value}
                                  lblOff=""
                                  lblOn=""
                                  disabled={pricingModel !== 'ONE_PAY'}
                                />
                              )}
                            />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {errors.paymentMethods && (
                    <Typography color="error" variant="caption" style={{ marginTop: 8, display: 'block' }}>
                      {errors.paymentMethods.card?.message ||
                        errors.paymentMethods.paymentButton?.message ||
                        errors.paymentMethods.transfer?.message}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}><Divider className={classes.divider} /></Grid>

              {/* ═══════════════ SECCIÓN: ETIQUETAS Y CARACTERÍSTICAS ═══════════════ */}
              <Grid item xs={12}>
                <Box className={classes.sectionHeader}>
                  <LabelIcon className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Etiquetas y Características
                  </Typography>
                </Box>
                <Box className={classes.sectionCard}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography className={classes.fieldLabel}>
                        Texto del menu cuando NO está solicitado
                        {' '}<span style={{ color: '#d61103' }}>*</span>
                      </Typography>
                      <Controller
                        name="labels.notRequested"
                        control={control}
                        rules={{ 
                          required: 'El texto cuando no está solicitado es requerido',
                          maxLength: { value: 60, message: 'No puede exceder 60 caracteres' }
                        }}
                        render={({ value, onChange }) => (
                          <TextField
                            value={value || ''}
                            onChange={onChange}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="Ej: Solicita tu Terminal"
                            error={Boolean(errors.labels?.notRequested)}
                            helperText={errors.labels?.notRequested?.message}
                            inputProps={{ maxLength: 60 }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography className={classes.fieldLabel}>
                        Texto cuando SÍ está solicitado
                        {' '}<span style={{ color: '#d61103' }}>*</span>
                      </Typography>
                      <Controller
                        name="labels.requested"
                        control={control}
                        rules={{ 
                          required: 'El texto cuando está solicitado es requerido',
                          maxLength: { value: 60, message: 'No puede exceder 60 caracteres' }
                        }}
                        render={({ value, onChange }) => (
                          <TextField
                            value={value || ''}
                            onChange={onChange}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="Ej: Terminal Billet"
                            error={Boolean(errors.labels?.requested)}
                            helperText={errors.labels?.requested?.message}
                            inputProps={{ maxLength: 60 }}
                          />
                        )}
                      />
                    </Grid>

                    {/* Features Dinámicas */}
                    <Grid item xs={12}>
                      <Typography className={classes.fieldLabel} style={{ marginTop: 8 }}>
                        Características del Producto
                        {' '}<span style={{ color: '#d61103' }}>*</span>
                      </Typography>

                      <Typography className={classes.fieldHint} style={{ marginBottom: 8 }}>
                        Agregue las características que se mostrarán al usuario en la app (máximo 4). Al menos una característica es requerida.
                      </Typography>

                      {fields.map((field, index) => (
                        <Box key={field.id} className={classes.featureItem}>
                          <Controller
                            name={`features.${index}.value`}
                            control={control}
                            rules={{ 
                              required: 'La característica es requerida',
                              maxLength: { value: 260, message: 'No puede exceder 260 caracteres' }
                            }}
                            render={({ value, onChange }) => (
                              <TextField
                                value={value || ''}
                                onChange={onChange}
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder={`Característica ${index + 1}`}
                                error={Boolean(errors.features?.[index]?.value)}
                                helperText={errors.features?.[index]?.value?.message}
                                inputProps={{ maxLength: 260 }}
                              />
                            )}
                          />
                          {fields.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => remove(index)}
                              style={{ color: '#C62828' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => append({ value: '' })}
                        variant="outlined"
                        size="small"
                        className={`${classes.addButton} ${classes.greenOutlinedButton}`}
                        disabled={fields.length >= 4}
                      >
                        Agregar característica
                      </Button>
                      {fields.length >= 4 && (
                        <Typography className={classes.fieldHint} style={{ marginTop: 8, color: '#E45501' }}>
                          Se ha alcanzado el límite máximo de 4 características.
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Debug Section - Footer */}
              {/* <Grid item xs={12}>
                <Box className={classes.sectionCard} style={{ backgroundColor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: '#666' }}>
                    Debug - Payload de envío (Tiempo Real)
                  </Typography>

                  <pre style={{
                    backgroundColor: '#1e1e1e',
                    color: '#d4d4d4',
                    padding: '12px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '11px',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    maxHeight: '400px',
                    margin: 0,
                  }}>
                    {JSON.stringify({
                      ...(formValues.sk ? { sk: formValues.sk } : {}),
                      product_name: formValues.product_name || '',
                      product_type: formValues.product_type || '',
                      config: {
                        base: {
                          type: [formValues.terminalType || ''],
                          acquisition: {
                            payment_methods_supported: (formValues.paymentMethodsOrder || ['card', 'paymentButton', 'transfer'])
                              .map((key: string, index: number) => ({
                                method: key === 'card' ? 'CREDIT_CARD' : key === 'paymentButton' ? 'BHD_BUTTON' : 'BANK_TRANSFER',
                                order: index + 1,
                                status: formValues.pricingModel === 'ONE_PAY' && formValues.paymentMethods?.[key] ? 'ACTIVE' : 'INACTIVE',
                              })),
                          },
                          active: formValues.active || false,
                          currency: 'DOP',
                          description: formValues.description || '',
                          fulfillment_type: formValues.terminalType === 'DISPOSITIVO' ? 'DELIVERY' : 'DIGITAL',
                          labels: {
                            not_requested: formValues.labels?.notRequested || '',
                            features: (formValues.features || [])
                              .map((f: any) => typeof f === 'string' ? f : f.value)
                              .filter((f: string) => f && f.trim() !== ''),
                            requested: formValues.labels?.requested || '',
                          },
                          product_type: formValues.product_type || '',
                          requires_fulfillment: formValues.terminalType === 'DISPOSITIVO',
                          status: formValues.active ? 'ACTIVE' : 'INACTIVE',
                        },
                        pricing: {
                          amount: parseFloat(formValues.price) || 0,
                          currency: 'DOP',
                          model: [formValues.pricingModel || ''],
                          product_type: formValues.product_type || '',
                          status: 'ACTIVE',
                        },
                      },
                    }, null, 2)}
                  </pre>
                </Box>
              </Grid> */}
            </Grid>
          </Box>
        </ActionPopUp>

        <Toaster
          show={alert.show}
          type={alert.type}
          text={alert.text}
          onClose={() => setAlert({ show: false, type: 'success', text: '' })}
        />
      </SnackbarProvider>

      <Snackbar
        open={alert.show && alert.type === 'success'}
        autoHideDuration={2000}
        onClose={() => setAlert({ show: false, type: 'success', text: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity="success"
          onClose={() => setAlert({ show: false, type: 'success', text: '' })}
          style={{ backgroundColor: '#4CAF50', color: '#fff', fontWeight: 600 }}
        >
          {alert.text}
        </MuiAlert>
      </Snackbar>

      {/* Modal de advertencia — Último método de pago activo */}
      <Dialog open={showWarningModal} onClose={handleWarningClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gridGap={8}>
            <WarningRoundedIcon style={{ color: '#E45501', fontSize: 28 }} />
            <Typography variant="h6" component="span" style={{ fontWeight: 600 }}>
              Debe habilitar al menos un método de pago
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Para continuar, es necesario que exista al menos un método de pago activo en la
            configuración del cobro de la solicitud de link de pagos. Por favor, habilite un
            método de pago antes de guardar los cambios.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningClose} className={classes.greenButton} variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de advertencia — Solicitud gratuita */}
      <Dialog open={showFreeModal} onClose={handleFreeModalClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gridGap={8}>
            <WarningRoundedIcon style={{ color: '#E45501', fontSize: 28 }} />
            <Typography variant="h6" component="span" style={{ fontWeight: 600 }}>
              Solicitud gratuita
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Estás configurando esta solicitud como &quot;Gratis&quot;, por tanto el usuario
            estará exento del pago para esta solicitud.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFreeModalClose} className={classes.greenButton} variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentMethodForm;
